/**
 * Page-level chrome for the public (no-login) share view: the loading and
 * link-not-found states that bracket `PublicCampaignShell`. The login dialog
 * lives in `../components/common/PublicLoginDialog`. Kept in packages/ui so the
 * public surface shares one icon set and token vocabulary with the rest of the app.
 */
import * as React from 'react';

import { Lock } from '../design-system/icons';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

/**
 * Full-page loading state. Traces the shell's own silhouette (collapsed main
 * rail → campaign nav → top bar → body) so the layout doesn't jump when the
 * payload lands.
 */
export function PublicShareViewSkeleton(): React.ReactElement {
  return (
    <div data-testid="public-share-view-skeleton" className="flex min-h-screen bg-background">
      <div className="w-16 shrink-0 border-r border-border bg-card" />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="h-13 shrink-0 border-b border-border bg-white" />
        <div className="flex min-w-0 flex-1">
          <div className="hidden w-56 shrink-0 flex-col gap-2 border-r border-border p-3 md:flex">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-10 w-full rounded-lg" />
            ))}
          </div>
          <div className="flex min-w-0 flex-1 flex-col items-center">
            <div className="flex w-full max-w-[1200px] flex-col gap-5 p-8">
              <Skeleton className="h-7 w-64" />
              <Skeleton className="h-[420px] w-full rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Shown when the API 404s the link (unknown code, revoked, or expired — all
 * one 404 BE-side). Copy stays vague so it can't confirm the campaign exists.
 */
export function PublicSignInGate({
  onSignIn,
  title = 'Sign in to view this',
  description = "This link isn't available. It may have been revoked or expired — sign in to open the campaign directly.",
  isWrongAccount = false,
}: {
  onSignIn: () => void;
  /** Heading override for the sign-in state. Defaults to the public-share copy.
   *  Ignored when `isWrongAccount` is set (that state has its own fixed copy). */
  title?: string;
  /** Body copy override for the sign-in state. Defaults to the public-share
   *  (revoked/expired) copy. Ignored when `isWrongAccount` is set. */
  description?: string;
  /**
   * Set when the visitor is signed in but not authorized for this campaign —
   * swaps the copy from "sign in" to "switch account". Takes precedence over
   * `title`/`description`.
   */
  isWrongAccount?: boolean;
}): React.ReactElement {
  return (
    <div
      data-testid="public-sign-in-gate"
      className="flex min-h-screen flex-col items-center justify-center gap-3 bg-muted px-6 text-center"
    >
      <span className="flex size-14 items-center justify-center rounded-full bg-card text-muted-foreground shadow-sm">
        <Lock size={26} />
      </span>
      {isWrongAccount ? (
        <>
          <h1 className="text-heading-h6 font-semibold text-foreground">This account doesn&apos;t have access</h1>
          <p className="max-w-[380px] text-body-sm-regular text-muted-foreground">
            You&apos;re signed in, but this account doesn&apos;t have access to this campaign. Switch
            to an account that does to continue.
          </p>
          <Button type="button" onClick={onSignIn} className="mt-2">
            Switch account
          </Button>
        </>
      ) : (
        <>
          <h1 className="text-heading-h6 font-semibold text-foreground">{title}</h1>
          <p className="max-w-[380px] text-body-sm-regular text-muted-foreground">{description}</p>
          <Button type="button" onClick={onSignIn} className="mt-2">
            Sign in
          </Button>
        </>
      )}
    </div>
  );
}
